import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { MaskitoDirective } from '@maskito/angular';
import { InputSize } from '../../../enum/input-size.enum';
import { formatNumber } from '@angular/common';

@Component({
  selector: 'lib-currency-input',
  imports: [MatIcon, MaskitoDirective],
  templateUrl: './currency-input.component.html',
  styleUrl: './currency-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true,
    },
  ],
})
export class CurrencyInputComponent implements ControlValueAccessor {
  placeholder = input<string>('');
  autocomplete = input<string>('');
  controlId = input<string | null>(null);
  error = input<boolean>(false);
  inputSize = input<InputSize>(InputSize.large);
  allowNegative = input<boolean>(false);
  valueChange = output<number | null>();

  protected readonly InputSize = InputSize;

  public value = signal<string | null>(null);
  public disabled = signal(false);

  locale = inject(LOCALE_ID);

  protected readonly frenchCurrencyMaskitoOptions: MaskitoOptions = maskitoNumberOptionsGenerator({
    decimalSeparator: ',',
    min: this.allowNegative() ? -Infinity : 0,
    maximumFractionDigits: 2,
  });
  protected readonly englishCurrencyMaskitoOptions: MaskitoOptions = maskitoNumberOptionsGenerator({
    thousandSeparator: ',',
    min: this.allowNegative() ? -Infinity : 0,
    maximumFractionDigits: 2,
  });

  public writeValue(value: number | string | null): void {
    if (value === null || value === undefined || value === '') {
      this.value.set(null);
      return;
    }

    const numericValue = typeof value === 'number' ? value : this.parseToNumber(value);
    this.value.set(numericValue === null ? null : formatNumber(numericValue, this.locale, '1.2-2'));
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const numericValue = this.parseToNumber(inputValue);

    this.value.set(inputValue);
    this.valueChange.emit(numericValue);
    this.onChange(numericValue);
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onBlur(): void {
    this.onTouched();
    this.value.set(
      (numericValue => (numericValue === null ? null : formatNumber(numericValue, this.locale, '1.2-2')))(
        this.parseToNumber(this.value() ?? ''),
      ),
    );
  }

  private parseToNumber(rawValue: string): number | null {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return null;
    }

    // Remove regular and non-breaking spaces used as thousand separators.
    let normalized = trimmed.replace(/[\s\u00A0\u202F]/g, '');
    normalized = this.locale.startsWith('fr') ? normalized.replace(',', '.') : normalized.replace(/,/g, '');

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private onChange: (value: number | null) => void = () => {
    // default onChange
  };
  private onTouched: () => void = () => {
    // default onTouched
  };
}
